/**
 * Reducer para Chatbot Wizard v2
 * Maneja el estado del flujo guiado por JSON con state machine
 */

import {
  CHATBOT_TOGGLE,
  CHATBOT_SET_TYPING,
  CHATBOT_CLEAR_HISTORY,
  CHATBOT_LOAD_HISTORY,
  CHATBOT_NAVIGATE,
  CHATBOT_SET_STEP,
  CHATBOT_GO_BACK,
  CHATBOT_ADD_MESSAGE,
  CHATBOT_ADD_USER_MESSAGE,
  CHATBOT_ADD_BOT_MESSAGE,
  CHATBOT_SUBMIT_CONTACT,
  CHATBOT_UPDATE_CONTACT_FORM,
  CHATBOT_SET_TIMER,
  CHATBOT_TICK_TIMER,
  CHATBOT_CONTACT_REQUEST,
  CHATBOT_CONTACT_SUCCESS,
  CHATBOT_CONTACT_FAILURE,
} from './chatbot.actions';
import { LOGOUT_RESPONSE } from '../session/session.actions';

const initialState = {
  isOpen: false,
  currentStep: 'welcome',
  currentSection: null,
  currentQuestion: null,
  navigationStack: [],
  messages: [],
  contactForm: null,
  confirmationTimer: null,
  isTyping: false,
  isContactSubmitting: false,
  contactError: null,
};

const chatbotReducer = (state = initialState, action) => {
  // Validación defensiva: asegurar que navigationStack siempre sea array
  if (state && !Array.isArray(state.navigationStack)) {
    state = {
      ...state,
      navigationStack: [],
    };
  }

  switch (action.type) {
    case CHATBOT_TOGGLE:
      return {
        ...state,
        isOpen: action.payload.isOpen,
      };

    case CHATBOT_SET_TYPING:
      return {
        ...state,
        isTyping: action.payload.isTyping,
      };

    case CHATBOT_NAVIGATE: {
      const { step, section, question, keepSection, userMessage } =
        action.payload;

      console.log('🟡 Reducer CHATBOT_NAVIGATE:', {
        from: state.currentStep,
        to: step,
        section,
        question,
        userMessage,
      });

      // Construir nueva entrada para el stack
      const newStackItem = {
        step: state.currentStep,
        section: state.currentSection,
        question: state.currentQuestion,
      };

      // Agregar al stack solo si cambiamos de paso o sección significativamente
      const shouldPushToStack =
        state.currentStep !== 'welcome' &&
        (state.currentStep !== step || state.currentSection !== section);

      let newMessages = [...state.messages];

      // Agregar mensaje de usuario si viene en el payload
      if (userMessage) {
        // Si el mensaje es de una pregunta y no tiene sección, agregar la sección actual
        const messageRef = { ...userMessage.ref };
        if (
          messageRef.step === 'question' &&
          !messageRef.section &&
          state.currentSection
        ) {
          messageRef.section = state.currentSection;
        }

        newMessages.push({
          id: Date.now(),
          type: 'user',
          ref: messageRef,
          optionLabel: userMessage.optionLabel,
          timestamp: new Date().toISOString(),
        });
      }

      // Si navegamos a 'answer', agregar también el mensaje de respuesta del bot
      if (step === 'answer' && question) {
        newMessages.push({
          id: Date.now() + 1,
          type: 'bot',
          ref: {
            step: 'answer',
            section: keepSection ? state.currentSection : section,
            question: question,
          },
          timestamp: new Date().toISOString(),
        });
      }

      const newState = {
        ...state,
        currentStep: step,
        currentSection: keepSection ? state.currentSection : section ?? null,
        currentQuestion: question ?? null,
        navigationStack: shouldPushToStack
          ? [...state.navigationStack, newStackItem]
          : state.navigationStack,
        messages: newMessages,
        isTyping: false,
      };

      console.log('🟡 Nuevo estado:', {
        currentStep: newState.currentStep,
        messagesCount: newState.messages.length,
      });

      return newState;
    }

    case CHATBOT_SET_STEP:
      return {
        ...state,
        currentStep: action.payload.step,
      };

    case CHATBOT_GO_BACK: {
      if (state.navigationStack.length === 0) {
        // Si no hay historial, volver al inicio
        return {
          ...state,
          currentStep: 'welcome',
          currentSection: null,
          currentQuestion: null,
        };
      }

      // Obtener el último item del stack
      const previousItem =
        state.navigationStack[state.navigationStack.length - 1];
      const newStack = state.navigationStack.slice(0, -1);

      return {
        ...state,
        currentStep: previousItem.step,
        currentSection: previousItem.section ?? null,
        currentQuestion: previousItem.question ?? null,
        navigationStack: newStack,
      };
    }

    case CHATBOT_ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload.message],
      };

    case CHATBOT_ADD_USER_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.payload.id,
            type: 'user',
            ref: action.payload.ref,
            optionLabel: action.payload.optionLabel,
            timestamp: action.payload.timestamp,
          },
        ],
      };

    case CHATBOT_ADD_BOT_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.payload.id,
            type: 'bot',
            ref: action.payload.ref,
            timestamp: action.payload.timestamp,
          },
        ],
        isTyping: false,
      };

    case CHATBOT_UPDATE_CONTACT_FORM:
      return {
        ...state,
        contactForm: {
          ...state.contactForm,
          [action.payload.field]: action.payload.value,
        },
      };

    case CHATBOT_SUBMIT_CONTACT:
      return {
        ...state,
        contactForm: action.payload,
        // No transition here - middleware controls the flow via API call
      };

    case CHATBOT_CONTACT_REQUEST:
      return {
        ...state,
        isContactSubmitting: true,
        contactError: null,
      };

    case CHATBOT_CONTACT_SUCCESS:
      return {
        ...state,
        isContactSubmitting: false,
        currentStep: 'contact_confirm',
        confirmationTimer: 10,
      };

    case CHATBOT_CONTACT_FAILURE:
      return {
        ...state,
        isContactSubmitting: false,
        contactError: action.payload.error,
      };

    case CHATBOT_SET_TIMER:
      return {
        ...state,
        confirmationTimer: action.payload.seconds,
      };

    case CHATBOT_TICK_TIMER: {
      const newTimer =
        state.confirmationTimer !== null ? state.confirmationTimer - 1 : null;

      // Si el timer llega a 0, regresar al menú
      if (newTimer === 0) {
        return {
          ...state,
          currentStep: 'menu',
          currentSection: null,
          currentQuestion: null,
          confirmationTimer: null,
          contactForm: null,
        };
      }

      return {
        ...state,
        confirmationTimer: newTimer,
      };
    }

    case CHATBOT_CLEAR_HISTORY:
      return {
        ...initialState,
      };

    case CHATBOT_LOAD_HISTORY:
      return {
        ...state,
        isOpen: action.payload.isOpen || false,
        messages: action.payload.messages || [],
        currentStep: action.payload.currentStep || 'welcome',
        currentSection: action.payload.currentSection || null,
        currentQuestion: action.payload.currentQuestion || null,
        navigationStack: action.payload.navigationStack || [],
      };

    case LOGOUT_RESPONSE:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

export default chatbotReducer;
